/* eslint-disable jsx-a11y/control-has-associated-label */
import React, {
  useState,
  useEffect,
  useMemo,
  Suspense,
  useCallback
} from 'react';

import {
  Checkbox,
  LinearProgress,
  Fade,
  Tooltip,
  IconButton
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import moment from 'moment';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import Button from 'components/Button/button';
import CommonAlert from 'components/CommonAlert/CommonAlert';
import PurchaseHistory from 'components/Dialogs/PurchaseDialogs/PurchaseHistory';
import PurchaseProjection from 'components/Dialogs/PurchaseDialogs/PurchaseProjection';
import ImageWrapper from 'components/ImageWrapper';
import ItemsPerPage from 'components/ItemsPerPage';
import OrderBy from 'components/OrderBY';
import PageTitleAndFilter from 'components/PageTitleAndFilter';
import ReceivingStatus from 'components/ReceivingStatus';
import Layout from 'containers/layouts/layout';
import HistoryTime from 'images/icons/combinations/historyTimeWithBg.svg';
import { Container, GlobalStyle } from 'styles/general';
import Environment from 'utils/environments';
import {
  getFilterOptions,
  handleFilterOptions,
  handleMultipleFilters
} from 'utils/handleFilterActions';

const environments = Environment(process.env.REACT_APP_ENV);

const TheoryStockDialog = React.lazy(() =>
  import('components/Dialogs/ExtractsDialog')
);

const DurationDialog = React.lazy(() =>
  import('components/Dialogs/DurationDialog')
);

function PurchaseManager({ userInfo }) {
  const { id: paramsID } = useParams();
  const originId = paramsID;
  const stores = useState(
    userInfo.companies.groups
      .reduce((acc, group) => [...acc, group.subGroups], [])
      .flat()
      .reduce((acc, subGroup) => [...acc, subGroup.stores], [])
      .flat()
  );
  const groupId = userInfo?.companies?.groups?.[0]?.id;

  const [callSettings, setCallSettings] = useState({
    getId: paramsID,
    mainLoading: false,
    page: 1,
    totalPages: 0,
    size: 100,
    numberOfElements: 0,
    totalElements: 0
  });

  const [products, setProducts] = useState([]);
  const { page, size, totalPages, numberOfElements, totalElements } =
    callSettings;
  const [filtersChanged, setFiltersChanged] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderQuantities, setOrderQuantities] = useState({});
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [order, setOrder] = useState({ orderBy: '', orderClass: '' });
  const [alertSeverity, setAlertSeverity] = useState('error');
  const [isTheoryStockOpen, setIsTheoryStockOpen] = useState(false);
  const [safetyMarginShopping, setSafetyMarginShopping] = useState('');

  const userLocal = JSON.parse(localStorage.getItem('companies'));
  const user = userLocal?.user;

  const [modalStates, setModalStates] = useState({
    modalDuration: false
  });

  const [durationData, setDurationData] = useState({
    product: {},
    data: {},
    type: '',
    newDuration: false
  });

  const [projectionData, setProjectionData] = useState({
    totalProjection: 0,
    days: []
  });

  const [filterFields, setFilterFields] = useState([
    [
      {
        label: 'Categoria',
        filterName: 'categoryDescription',
        placeHolder: 'Selecione...',
        type: 'newTreeSelector',
        urlPath: `${environments.catalog}/categories`,
        key: 'categories',
        optionReference: 'description',
        options: [],
        size: 'medium',
        customSize: '200px',
        marginRight: '25px'
      },
      {
        label: 'Produto',
        filterName: 'productDescription',
        filterNameSecond: 'productId',
        placeHolder: 'Selecione...',
        searchByTermMode: true,
        type: 'autoComplete',
        urlPath: `${environments.catalog}/shopping/products?groupId=${groupId}`,
        options: [],
        size: 'medium',
        key: 'products',
        optionReference: 'description',
        customSize: '200px',
        marginRight: '25px'
      },
      {
        label: 'Restaurante',
        filterName: 'restaurantIds',
        placeHolder: 'Selecione...',
        type: 'autoComplete',
        key: 'stores',
        optionReference: 'name',
        options: stores[0],
        size: 'medium',
        disableGetOptionsOnFocus: true,
        allowClear: true,
        isSelect: true,
        customSize: '200px'
      },
      {
        label: 'Duração',
        filterName: 'duration',
        type: 'autoComplete',
        isSelect: true,
        allowClear: true,
        key: 'duration',
        customSize: '100px',
        defaultValue: { title: 'Todos', value: '' },
        options: [
          { title: 'Todos', value: '' },
          ...Array.from({ length: 7 }, (_, i) => ({
            title: `${i + 1}`,
            value: `${i + 1}`
          }))
        ],
        optionReference: 'title',
        marginRight: '25px'
      },
      {
        label: 'Pedido',
        filterName: 'toOrder',
        type: 'autoComplete',
        isSelect: true,
        allowClear: true,
        key: 'toOrder',
        defaultValue: { title: 'Todos', value: '' },
        options: [
          { title: 'Todos', value: '' },
          { title: 'Sim', value: 'true' },
          { title: 'Não', value: 'false' }
        ],
        optionReference: 'title',
        customSize: '100px',
        marginRight: '25px'
      },
      {
        label: 'Projeção',
        filterName: 'toProjection',
        type: 'autoComplete',
        isSelect: true,
        allowClear: true,
        key: 'toProjection',
        defaultValue: { title: 'Todos', value: '' },
        options: [
          { title: 'Todos', value: '' },
          { title: 'Sim', value: 'true' },
          { title: 'Não', value: 'false' }
        ],
        optionReference: 'title',
        customSize: '100px',
        marginRight: '25px'
      },
      {
        label: 'Status Pedido',
        filterName: 'status',
        placeHolder: 'Selecione...',
        type: 'autoComplete',
        isSelect: true,
        allowClear: true,
        key: 'status',
        customSize: '200px',
        options: [
          { title: 'Todos', value: '' },
          { title: 'Pedido', value: 'ORDER' },
          { title: 'Receber', value: 'WAITING' },
          { title: 'Recebido', value: 'RECEIVED' },
          { title: 'Não recebido', value: 'NOT RECEIVED' },
          { title: 'Devolvido', value: 'RETURNED' },
          { title: 'Cancelado', value: 'CANCELED' }
        ],
        optionReference: 'title'
      }
    ]
  ]);

  const fetchProjectionNext7Days = async () => {
    try {
      const res = await fetch(
        `${environments.restaurants}/salesProjection/nextDays?originId=${originId}`
      );
      if (!res.ok) throw new Error('Erro ao buscar projeção');

      const data = await res.json();
      setProjectionData({
        totalProjection: data.totalProjection || 0,
        days: data.days || []
      });
    } catch (err) {
      console.error('Erro ao buscar projeção:', err);
    }
  };

  useEffect(() => {
    fetchProjectionNext7Days();
  }, [originId]);

  const projectedTotalValue = useMemo(
    () =>
      projectionData.totalProjection.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }),
    [projectionData.totalProjection]
  );

  useEffect(() => {
    const fetchSafetyMargin = async () => {
      try {
        const response = await fetch(
          `${environments.restaurants}/parameters/${originId}`
        );
        const data = await response.json();
        setSafetyMarginShopping(
          data.salesProjection?.safetyMarginShopping || ''
        );
      } catch (err) {
        console.error('Failed to fetch safety margin:', err);
      }
    };

    fetchSafetyMargin();
  }, [originId]);

  const renderMetaText = () => (
    <>
      <div
        className="metaText dFlex"
        style={{ marginLeft: '16px', fontSize: '14px' }}
      >
        Sugestões baseadas em{' '}
        <strong style={{ color: '#5062F0', marginLeft: 4 }}>
          Meta Loja: {projectedTotalValue}
        </strong>
      </div>
      <div
        className="metaText dFlex"
        style={{ marginLeft: '16px', fontSize: '14px' }}
      >
        Margem de segurança (%):
             <span
        style={{
          marginLeft: 4,
          width: '34px',
          display: 'inline-block',
          borderBottom: '1px solid #5062F0',
          textAlign: 'right',
          fontSize: '14px',
          lineHeight: '20px',
          color: '#000'
        }}
      >
        {safetyMarginShopping}
      </span>
      </div>
    </>
  );

  const openTheoryStockDirect = (product) => {
    const restaurant = product.consume?.restaurants?.[0];
    if (!restaurant) return;

    setSelectedProduct({
      ...product,
      singleRestaurant: restaurant
    });
    setIsTheoryStockOpen(true);
  };

  const openDurationDialogDirect = async (product) => {
    const restaurant = product.consume?.restaurants?.[0];
    if (!restaurant) return;

    try {
      const stock =
        product?.stock?.restaurants?.find(
          (r) => r.restaurantId === restaurant.restaurantId
        )?.quantity ?? 0;

      const response = await fetch(
        `${environments.engine}/rupture?productId=${restaurant.productId}&restaurantId=${restaurant.restaurantId}&days=8&stock=${stock}`
      );
      const ruptureData = await response.json();

      setDurationData({
        product: {
          ...product,
          consumptionUnits: product?.units
        },
        data: ruptureData,
        type: 'duration',
        newDuration: false
      });

      setModalStates({ modalDuration: true });
    } catch (err) {
      console.error('Erro ao carregar dados de duração direta:', err);
    }
  };

  const openProjectionModal = (product) => {
    setSelectedProduct(product);

    const initialQuantities = {};
    product.order?.restaurants?.forEach((restaurant) => {
      initialQuantities[restaurant.restaurantId] = restaurant.quantity ?? 0;
    });

    setOrderQuantities(initialQuantities);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOrderQuantities({});
  };

  const handleOrderChange = (restaurantId, value) => {
    setOrderQuantities((prev) => ({
      ...prev,
      [restaurantId]: value
    }));
  };

  const handleSaveQuantities = async () => {
    const payload = [];

    selectedProduct?.consume?.restaurants?.forEach((restaurant) => {
      const newQuantity = orderQuantities[restaurant.restaurantId];

      const originalQuantity =
        selectedProduct?.order?.restaurants?.find(
          (o) => o.restaurantId === restaurant.restaurantId
        )?.quantity ?? 0;

      const hasChanged =
        typeof newQuantity === 'number' &&
        parseFloat(newQuantity.toFixed(3)) !==
          parseFloat(originalQuantity.toFixed(3));

      if (hasChanged && newQuantity > 0) {
        payload.push({
          productId: restaurant.productId,
          quantity: newQuantity,
          user
        });
      }
    });

    if (payload.length === 0) {
      setAlertMessage('Nenhuma alteração detectada.');
      setAlertOpen(true);
      setAlertSeverity('info');
      return;
    }

    try {
      const response = await fetch(
        `${environments.catalog}/shopping/products`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) throw new Error('Erro ao salvar os pedidos');

      setIsModalOpen(false);
      setAlertMessage('Pedido salvo com sucesso!');
      setAlertOpen(true);
      setAlertSeverity('success');
    } catch (error) {
      setAlertMessage('Erro ao salvar pedido');
      setAlertOpen(true);
      setAlertSeverity('error');
    }
  };

  const updateProductOrderQuantities = (productId, updatedRestaurants) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.id !== productId) return product;

        const updatedOrder = {
          ...product.order,
          restaurants: updatedRestaurants,
          total: updatedRestaurants.reduce(
            (sum, r) => sum + (r.quantity ?? 0),
            0
          )
        };

        return {
          ...product,
          order: updatedOrder
        };
      })
    );
  };

  const handleGenerateOrder = async () => {
    const payload = {
      user,
      groupId,
      productIds: selectedProductIds
    };

    try {
      const response = await fetch(`${environments.catalog}/shopping/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Erro ao gerar o pedido');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pedido.xls');
      document.body.appendChild(link);
      link.click();
      link.remove();

      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          if (selectedRowIds.includes(product.id)) {
            const today = moment().format('YYYY-MM-DD');

            const updatedRestaurants =
              product.order?.restaurants?.map((r) => ({
                ...r,
                date: today
              })) || [];

            return {
              ...product,
              receive: {
                status: 'ORDER',
                date: today,
                restaurants: updatedRestaurants,
                total: updatedRestaurants.length,
                current: updatedRestaurants.length
              }
            };
          }
          return product;
        })
      );
      setSelectedProductIds([]);

      setAlertMessage('Pedido gerado com sucesso!');
      setAlertSeverity('success');
      setAlertOpen(true);
    } catch (error) {
      setAlertMessage('Erro ao gerar pedido');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const fetchPurchaseHistory = async () => {
    try {
      const response = await fetch(
        `${environments.catalog}/shopping/orders?groupId=${groupId}&page=1&size=50`
      );
      if (!response.ok) throw new Error('Erro ao buscar histórico de pedidos');

      const data = await response.json();

      const history = data.map((item) => ({
        id: item.id,
        date: moment(item.date).format('DD/MM/YYYY - HH:mm[hs]'),
        products: item.quantityProducts
      }));

      setPurchaseHistory(history);
    } catch (error) {
      console.error('Erro ao buscar histórico de pedidos:', error);
    }
  };

  const handleFilter = useCallback(
    (param, value, filter, paramSecond) => {
      const hasFilterWithFirstName = filtersChanged?.filter(
        (el) => el?.param === param
      )?.length;
      const hasFilterWithSecondName = filtersChanged?.filter(
        (el) => el?.param === paramSecond
      )?.length;

      let formattedFilters = hasFilterWithSecondName
        ? filtersChanged?.filter((el) => el?.param !== paramSecond)
        : filtersChanged;

      if (!value) {
        if (hasFilterWithFirstName || hasFilterWithSecondName) {
          formattedFilters = filtersChanged?.filter(
            (el) => el?.param !== paramSecond && el?.param !== param
          );
        }
      }

      handleFilterOptions(param, value, formattedFilters, setFiltersChanged);
    },
    [filtersChanged]
  );

  const getFilterhandle = (urlPath, label, key, optionReference) => {
    getFilterOptions(
      urlPath,
      label,
      key,
      filterFields,
      setFilterFields,
      optionReference,
      paramsID,
      '',
      1
    );
  };

  const handleOrderBy = (paramsAndValues) => {
    handleMultipleFilters(paramsAndValues, filtersChanged, setFiltersChanged);
  };

  const formatDecimal = (value) =>
    (value ?? 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });

  const fetchProducts = async () => {
    setCallSettings((prev) => ({ ...prev, mainLoading: true }));

    const params =
      filtersChanged
        ?.filter((el) => el?.value)
        .map((filter) => filter.urlParam)
        .join('&') || '';

    try {
      const res = await fetch(
        `${environments.catalog}/shopping?page=${page}&size=${size}&originId=${paramsID}&groupId=${groupId}&${params}`
      );

      const data = await res.json();
      const productsData = Array.isArray(data.content) ? data.content : [];

      setProducts(productsData);
      setCallSettings((prev) => ({
        ...prev,
        mainLoading: false,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        numberOfElements: data.numberOfElements
      }));
    } catch (err) {
      console.error('Erro ao buscar produtos da API:', err);
      setCallSettings((prev) => ({ ...prev, mainLoading: false }));
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [size, page, filtersChanged, order]);

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => setAlertOpen(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [alertOpen]);

  return (
    <Layout>
      <GlobalStyle />
      <Container>
        <PageTitleAndFilter
          title="Compras próximos 7 dias"
          activeFilterFilds={filterFields}
          additionalbutton={renderMetaText}
          getFilterhandle={getFilterhandle}
          handleFilter={handleFilter}
        />

        <div className="dFlex alignCenter justifyEnd mb16">
          <span
            style={{
              color: '#5062F0',
              fontWeight: 'bold',
              paddingRight: '10px',
              fontSize: '16px'
            }}
          >
            {selectedRowIds.length} Produtos selecionados
          </span>

          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              design="contained"
              label="Gerar Pedido"
              className="defaultButton submitButton"
              style={{ width: '150px', height: '33px' }}
              onClick={handleGenerateOrder}
              disabled={selectedRowIds.length === 0}
            />
            <IconButton
              color="inherit"
              onClick={() => {
                fetchPurchaseHistory();
                setIsHistoryModalOpen(true);
              }}
              aria-label="close"
              style={{ outline: '0', background: 'none', padding: '0' }}
            >
              <img src={HistoryTime} alt="Histórico de pedidos" />
            </IconButton>
          </div>
        </div>
        <table className="desktop" cellPadding="0" cellSpacing="0" border="0">
          <thead>
            <tr className="headerDark">
              <th width="2%" className="subtitle pr0">
                &nbsp;
              </th>
              <th width="25%" className="subtitle">
                PRODUTO{' '}
                <OrderBy
                  filterName="description"
                  handleFilter={handleOrderBy}
                />
              </th>

              <th width="5%" className="subtitle center">
                UN.
              </th>
              <th width="10%" className="subtitle left p12 borderLGray">
                CATEGORIA{' '}
                <OrderBy filterName="category" handleFilter={handleOrderBy} />
              </th>
              <th width="8%" className="subtitle right pl10 borderLGray">
                CONSUMO{' '}
                <OrderBy filterName="consume" handleFilter={handleOrderBy} />
              </th>
              <th width="8%" className="subtitle right pl10 borderLGray">
                ESTOQUE{' '}
                <OrderBy filterName="stock" handleFilter={handleOrderBy} />
              </th>
              <th width="7%" className="subtitle center pl12 borderLGray">
                DURAÇÃO{' '}
                <OrderBy filterName="duration" handleFilter={handleOrderBy} />
              </th>
              <th
                width="61%"
                colSpan="3"
                className="subtitle center"
                style={{ background: '#5062F0' }}
              >
                PROJEÇÃO E PEDIDO
              </th>
            </tr>

            <tr className="headerSubtitle">
              <th colSpan="3" className="borderLGray">
                &nbsp;
              </th>
              <th colSpan="1" className="borderLGray">
                &nbsp;
              </th>
              <th className="subtitle right p12 borderLGray">ÚLTIMOS 7 DIAS</th>
              <th className="subtitle right p12 borderLGray">ATUAL</th>
              <th
                className="subtitle right p12 borderLGray"
                style={{ borderRight: 'solid 2px #5062F0' }}
              >
                DIAS
              </th>
              <th
                className="subtitle center"
                style={{
                  width: '10%',
                  borderRight: 'solid 1px #7583F0',
                  borderLeft: 'solid 2px #5062F0',
                  background: '#E5E7FD',
                  paddingLeft: '10px'
                }}
              >
                PRÓXIMOS 7 DIAS{' '}
                <OrderBy filterName="projection" handleFilter={handleOrderBy} />
              </th>
              <th
                className="subtitle center "
                style={{
                  width: '10%',
                  background: '#E5E7FD',
                  paddingLeft: '10px'
                }}
              >
                QTDE PEDIDO{' '}
                <OrderBy filterName="order" handleFilter={handleOrderBy} />
              </th>
              <th
                className="subtitle center borderLGray"
                style={{ width: '44%', background: '#E5E7FD' }}
              >
                STATUS RECEBIMENTO
              </th>
            </tr>
          </thead>

          <tbody>
            {callSettings.mainLoading ? (
              <tr>
                <th colSpan="20" className="m10 textAlignCenter">
                  <LinearProgress style={{ height: '7px' }} variant="query" />
                </th>
              </tr>
            ) : (
              products.map((product) => {
                const hasSingleRestaurant =
                  product.consume?.restaurants?.length === 1;

                return (
                  <tr
                    key={product.id}
                    style={{ borderBottom: '1px solid #D2D3D7' }}
                  >
                    <td
                      className="lineBorder dFlex alignCenter"
                      style={{ paddingTop: '8px' }}
                    >
                      <Checkbox
                        checked={selectedRowIds.includes(product.id)}
                        onChange={(e) => {
                          const associatedProductIds = product.productIds || [];
                          const isChecked = e.target.checked;

                          if (isChecked) {
                            const hasOrder = product?.order?.total > 0;

                            if (!hasOrder) {
                              setAlertMessage(
                                `Produto "${product.description}" sem quantidade de pedido.`
                              );
                              setAlertOpen(true);
                              return;
                            }

                            setSelectedRowIds((prev) => [...prev, product.id]);
                            setSelectedProductIds((prev) => {
                              const merged = [...prev, ...associatedProductIds];
                              return Array.from(new Set(merged));
                            });
                          } else {
                            setSelectedRowIds((prev) =>
                              prev.filter((id) => id !== product.id)
                            );
                            setSelectedProductIds((prev) =>
                              prev.filter(
                                (id) => !associatedProductIds.includes(id)
                              )
                            );
                          }
                        }}
                      />
                    </td>

                    <td
                      className="description left pr8"
                      style={{ maxWidth: '195px', minWidth: '195px' }}
                    >
                      <div className="dFlex alignCenter w100">
                        <div className="dFlex alignCenter justifyBetween w100">
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            placement="right"
                            title={
                              <span
                                className="dBlock"
                                style={{ padding: '4px 7px' }}
                              >
                                {product?.id} - {product?.description}
                              </span>
                            }
                          >
                            <span className="textEllipsis">
                              {product?.description}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    </td>

                    <td
                      className="description center"
                      style={{ maxWidth: '100px', paddingRight: '10px' }}
                    >
                      <div className="dFlex alignCenter justifyCenter">
                        <span className="p12">
                          {product.units?.abbreviation}
                        </span>
                        <ImageWrapper
                          width="30px"
                          height="28px"
                          url={product.image?.url}
                          skuId={product?.id}
                          skusImageUrls={[
                            { image: { url: product.image?.url } }
                          ]}
                          screen="product"
                          borderType="EXTERNAL"
                        />
                      </div>
                    </td>

                    <td
                      className="description left p12 borderLGray"
                      style={{ maxWidth: '160px' }}
                    >
                      <Tooltip
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 600 }}
                        placement="top"
                        title={
                          <span style={{ padding: '4px 7px' }}>
                            {product.category?.description || '-'}
                          </span>
                        }
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                            cursor: 'default'
                          }}
                        >
                          {product.category?.description || '-'}
                        </span>
                      </Tooltip>
                    </td>

                    <td
                      className="description right borderLGray p12 cursorPointer"
                      style={{ color: '#5062F0' }}
                      onClick={() => openProjectionModal(product)}
                    >
                      {formatDecimal(product.consume.total)}
                    </td>

                    <td
                      className="description right borderLGray p12 cursorPointer"
                      style={{ color: '#5062F0' }}
                      onClick={() =>
                        hasSingleRestaurant
                          ? openTheoryStockDirect(product)
                          : openProjectionModal(product)
                      }
                    >
                      {formatDecimal(product.stock.total)}
                    </td>

                    <td
                      className="description right borderLGray p12 cursorPointer"
                      onClick={() =>
                        hasSingleRestaurant
                          ? openDurationDialogDirect(product)
                          : openProjectionModal(product)
                      }
                      style={{
                        color:
                          product.duration.quantity === 0
                            ? '#D93030'
                            : '#5062F0'
                      }}
                    >
                      {product.duration.quantity}{' '}
                      {product.duration.quantity === 1 ? 'dia' : 'dias'}
                    </td>
                    <td
                      className="description right p12 cursorPointer"
                      onClick={() => openProjectionModal(product)}
                      style={{
                        borderLeft: '2px solid #5062F0',
                        borderRight: '1px solid #7583F0',
                        color: '#5062F0',
                        fontWeight: 'bold'
                      }}
                    >
                      {formatDecimal(product.projection.total)}{' '}
                      <span style={{ color: '#9296AC' }}>
                        / {product.projection?.restaurants?.length || 0}
                      </span>
                    </td>

                    <td
                      className="description right p12 cursorPointer"
                      onClick={() => openProjectionModal(product)}
                    >
                      <Tooltip
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 600 }}
                        placement="bottom-end"
                        title={
                          product.order?.date ? (
                            <div>
                              Pedido:
                              <br />
                              {moment(product.order.date).format('DD/MM/YYYY')}
                            </div>
                          ) : (
                            'Sem pedido'
                          )
                        }
                      >
                        <span>
                          {formatDecimal(
                            product.order?.restaurants?.reduce(
                              (sum, r) => sum + (r.quantity ?? 0),
                              0
                            )
                          )}
                          <span style={{ color: '#9296AC' }}>
                            / {product.order?.restaurants?.length || 0}
                          </span>
                        </span>
                      </Tooltip>
                    </td>

                    <td className="description center borderLGray p12">
                      <ReceivingStatus
                        status={product.receive?.status}
                        date={product.receive?.date}
                        restaurants={product.receive?.restaurants || []}
                        consumeRestaurants={product.consume?.restaurants || []}
                      />
                    </td>
                  </tr>
                );
              })
            )}

            <tr>
              <td colSpan="20" className="m10 textAlignCenter">
                <hr style={{ marginTop: '-1px' }} />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="pagination">
          <Pagination
            count={totalPages}
            onChange={(event, nextPage) => {
              setCallSettings((prev) => ({ ...prev, page: nextPage }));
            }}
            variant="outlined"
            shape="rounded"
            size="small"
            color="primary"
          />

          <ItemsPerPage
            numberOfElements={numberOfElements}
            size={totalElements}
          />
        </div>
      </Container>

      <PurchaseProjection
        open={isModalOpen}
        onClose={handleCloseModal}
        product={selectedProduct}
        orderQuantities={orderQuantities}
        setOrderQuantities={setOrderQuantities}
        handleOrderChange={handleOrderChange}
        onSave={handleSaveQuantities}
        updateProductOrderQuantities={updateProductOrderQuantities}
        refreshProducts={fetchProducts}
        groupId={groupId}
      />

      <PurchaseHistory
        open={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        historyData={purchaseHistory}
      />

      <Suspense fallback={<div />}>
        {isTheoryStockOpen && selectedProduct && (
          <TheoryStockDialog
            openModal={{ modalProduct: true }}
            handleClose={() => setIsTheoryStockOpen(false)}
            getId={[selectedProduct.singleRestaurant.restaurantId]}
            checkConsume={false}
            setCheckConsume={() => {}}
            productExtractDescription={selectedProduct.description}
            productModal={{
              id: selectedProduct.singleRestaurant.productId,
              description: selectedProduct.description,
              restaurantId: [selectedProduct.singleRestaurant.restaurantId],
              consumptionUnitsPrimary: selectedProduct.units
            }}
            setProductModal={() => {}}
            parameters=""
            originPage="purchaseProjection"
          />
        )}
      </Suspense>

      <Suspense fallback={<span />}>
        <DurationDialog
          openModal={modalStates}
          handleClose={() => setModalStates({ modalDuration: false })}
          durationData={durationData}
          loading={false}
          page="PurchaseProjection"
          selectedProduct={selectedProduct}
          setDurationData={setDurationData}
        />
      </Suspense>

      <CommonAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        messagePersonal={alertMessage}
        severity={alertSeverity}
      />
    </Layout>
  );
}

const mapStateToProps = (state) => ({
  userInfo: state.user
});

export default connect(mapStateToProps)(PurchaseManager);
